import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { getProducts, listReviews, submitReview } from "../lib/api";

const Star = ({ filled, size = 18 }) => (
    <span
        aria-hidden="true"
        style={{ fontSize: size, color: filled ? "#6A7059" : "#D9D3C6", lineHeight: 1 }}
    >
        {filled ? "★" : "☆"}
    </span>
);

const StarRating = ({ value, onChange, readOnly = false }) => (
    <div className="flex gap-1" data-testid={readOnly ? "review-stars" : "review-form-rating"}>
        {[1, 2, 3, 4, 5].map((n) => (
            <button
                key={n}
                type="button"
                disabled={readOnly}
                onClick={() => !readOnly && onChange && onChange(n)}
                className={readOnly ? "cursor-default" : "cursor-pointer hover:opacity-80"}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                data-testid={readOnly ? null : `review-form-rating-${n}`}
            >
                <Star filled={n <= value} size={readOnly ? 16 : 22} />
            </button>
        ))}
    </div>
);

const ReviewCard = ({ r }) => {
    const date = r.created_at ? new Date(r.created_at).toLocaleDateString("en-AU", {
        year: "numeric", month: "long", day: "numeric",
    }) : "";
    return (
        <article className="border border-[#E3DED5] bg-white p-8" data-testid={`review-card-${r.id}`}>
            <StarRating value={r.rating} readOnly />
            <h3 className="font-serif text-2xl mt-4 text-[#1C1C1A]" data-testid="review-title">
                {r.title}
            </h3>
            <p className="mt-3 text-[#4A4A46] leading-relaxed" data-testid="review-body">
                {r.body}
            </p>
            <div className="mt-6 pt-4 border-t border-[#E3DED5] flex flex-wrap justify-between gap-3 text-xs uppercase tracking-[0.22em] text-[#737562]">
                <span data-testid="review-name">{r.display_name}</span>
                <span data-testid="review-product">{r.product_name}</span>
                <span data-testid="review-date">{date}</span>
            </div>
        </article>
    );
};

const Reviews = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = new URLSearchParams(location.search);
    const initialBar = params.get("bar") || "";

    const [products, setProducts] = useState([]);
    const [reviews, setReviews] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    // form state
    const [form, setForm] = useState({
        name: "", email: "", rating: 5, title: "", body: "",
        product_slug: initialBar || "general", consent: false, website: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        let active = true;
        Promise.all([getProducts(), listReviews()])
            .then(([p, r]) => { if (active) { setProducts(p); setReviews(r); setLoading(false); } })
            .catch(() => setLoading(false));
        return () => { active = false; };
    }, []);

    useEffect(() => {
        // if URL ?bar= changes (e.g. arriving from a bar page), reflect it in the form + jump to form
        if (initialBar) {
            setForm((f) => ({ ...f, product_slug: initialBar }));
            setTimeout(() => {
                const el = document.getElementById("leave-a-review");
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 250);
        }
    }, [initialBar]);

    const filtered = useMemo(() => {
        if (filter === "all") return reviews;
        return reviews.filter((r) => r.product_slug === filter);
    }, [reviews, filter]);

    const filterOptions = useMemo(() => {
        const opts = [["all", "All Reviews"], ["general", "General ALATI Reviews"]];
        for (const p of products) opts.push([p.slug, p.name]);
        return opts;
    }, [products]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.consent) {
            toast.error("Please confirm permission to display the review.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await submitReview(form);
            setSubmitted(true);
            toast.success(res.message || "Thank you.");
            setForm({
                name: "", email: "", rating: 5, title: "", body: "",
                product_slug: "general", consent: false, website: "",
            });
            // clear ?bar= from url
            if (initialBar) navigate("/reviews", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join("; ") : (detail || err.message || "Something went wrong.");
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="reviews-page" className="bg-[#FAF9F6] min-h-screen">
            <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-12">
                <p className="label">Reviews</p>
                <h1 className="font-serif text-5xl sm:text-6xl mt-5 text-[#1C1C1A] leading-none" data-testid="reviews-heading">
                    Customer Reviews
                </h1>
                <p className="mt-5 max-w-2xl text-[#4A4A46] leading-relaxed">
                    Read what customers have shared about their ALATI bars, or leave a review of your own.
                </p>
            </div>

            {/* Filters */}
            <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-6 border-b border-[#E3DED5]">
                <div className="flex flex-wrap gap-x-6 gap-y-3" data-testid="reviews-filters">
                    {filterOptions.map(([val, label]) => (
                        <button
                            key={val}
                            onClick={() => setFilter(val)}
                            className={`text-xs uppercase tracking-[0.22em] pb-1 transition-colors ${
                                filter === val
                                    ? "text-[#1C1C1A] border-b-2 border-[#1C1C1A]"
                                    : "text-[#737562] hover:text-[#1C1C1A]"
                            }`}
                            data-testid={`reviews-filter-${val}`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Reviews list */}
            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
                {loading ? (
                    <p className="text-[#737562]">Loading&hellip;</p>
                ) : filtered.length === 0 ? (
                    <div className="border border-dashed border-[#C7C0B3] p-16 text-center bg-white" data-testid="reviews-empty">
                        <p className="font-serif text-2xl text-[#1C1C1A]">
                            ALATI reviews will appear here as customers begin sharing their experience.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="reviews-grid">
                        {filtered.map((r) => <ReviewCard key={r.id} r={r} />)}
                    </div>
                )}
            </div>

            {/* Leave a review */}
            <div id="leave-a-review" className="bg-[#F0EBE1] border-t border-[#E3DED5]">
                <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20">
                    <p className="label">Leave a Review</p>
                    <h2 className="font-serif text-3xl sm:text-4xl mt-5 text-[#1C1C1A] leading-snug">
                        Share your experience.
                    </h2>
                    <p className="mt-4 text-[#4A4A46] leading-relaxed">
                        Reviews are read personally and appear after approval. Your email address remains private and will never appear on the website.
                    </p>

                    {submitted ? (
                        <div className="mt-10 border border-[#C7C0B3] bg-white p-8" data-testid="review-submitted-message">
                            <p className="font-serif text-xl text-[#1C1C1A]">
                                Thank you. Your review has been received and will appear after it has been approved.
                            </p>
                            <button
                                onClick={() => setSubmitted(false)}
                                className="mt-6 text-xs uppercase tracking-[0.22em] text-[#6A7059] hover:text-[#1C1C1A]"
                                data-testid="review-submit-another"
                            >
                                Leave Another Review
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="review-form">
                            {/* Honeypot — hidden from real users, visible to bots */}
                            <input
                                type="text"
                                tabIndex={-1}
                                autoComplete="off"
                                value={form.website}
                                onChange={(e) => setForm({ ...form, website: e.target.value })}
                                style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }}
                                aria-hidden="true"
                                data-testid="review-form-honeypot"
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <label className="block">
                                    <span className="label mb-2 block">Name</span>
                                    <input
                                        required maxLength={80}
                                        className="input"
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        data-testid="review-form-name"
                                    />
                                </label>
                                <label className="block">
                                    <span className="label mb-2 block">Email (private)</span>
                                    <input
                                        required type="email"
                                        className="alati-input"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        data-testid="review-form-email"
                                    />
                                    <p className="mt-2 text-xs text-[#737562] leading-relaxed" data-testid="review-form-email-privacy-note">
                                        Your email address is used only to manage your review and will never be displayed publicly.
                                    </p>
                                </label>
                            </div>

                            <label className="block">
                                <span className="label mb-2 block">Bar</span>
                                <select
                                    className="alati-input"
                                    value={form.product_slug}
                                    onChange={(e) => setForm({ ...form, product_slug: e.target.value })}
                                    data-testid="review-form-product"
                                >
                                    <option value="general">General ALATI Review</option>
                                    {products.map((p) => (
                                        <option key={p.slug} value={p.slug}>{p.name}</option>
                                    ))}
                                </select>
                            </label>

                            <div className="block">
                                <span className="label mb-2 block">Rating</span>
                                <StarRating value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
                            </div>

                            <label className="block">
                                <span className="label mb-2 block">Review title</span>
                                <input
                                    required maxLength={100}
                                    className="alati-input"
                                    value={form.title}
                                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    data-testid="review-form-title"
                                />
                            </label>

                            <label className="block">
                                <span className="label mb-2 block">Your review</span>
                                <textarea
                                    required maxLength={2000} rows={6}
                                    className="alati-input resize-none"
                                    value={form.body}
                                    onChange={(e) => setForm({ ...form, body: e.target.value })}
                                    data-testid="review-form-body"
                                />
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={form.consent}
                                    onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                                    className="mt-1"
                                    data-testid="review-form-consent"
                                />
                                <span className="text-sm text-[#4A4A46] leading-relaxed">
                                    I confirm that ALATI may display this review publicly. My email address will remain private.
                                </span>
                            </label>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="btn-primary disabled:opacity-60"
                                    data-testid="review-form-submit"
                                >
                                    {submitting ? "Submitting…" : "Submit Review"}
                                </button>
                                <p className="mt-4 text-xs text-[#737562] leading-relaxed max-w-xl" data-testid="review-form-submit-privacy-note">
                                    Review submissions are checked for spam. Your name, email address and technical information such as your IP address may be stored for moderation and security purposes. See our <Link to="/privacy" className="alati-link">Privacy Policy</Link> for details.
                                </p>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Reviews;
